import type { FunctionComponent, ReactNode } from 'react';

import { DottedOutlineCircleIcon, IconCircleCheck } from '~/ui/core/icons';

import { PublishProgressIcon } from '../../components/PublishProgressIcon';
import { ReviewProgressIcon } from '../../components/ReviewProgressIcon';
import type { AuditStepId, AuditTrailStep } from './types';

const renderColoredGlyph = (stepId: AuditStepId, toneClass: string): ReactNode => (
    <span aria-hidden={true} className={`inline-flex items-center justify-center ${toneClass}`}>
        {stepId === 'reviewed' ? (
            <ReviewProgressIcon colorBase="currentColor" colorFill="currentColor" isActive={false} progress={1} />
        ) : (
            <PublishProgressIcon colorBase="currentColor" colorFill="currentColor" isActive={false} progress={1} />
        )}
    </span>
);

const renderActiveGateStamp = (step: AuditTrailStep): ReactNode => {
    const toneClass = step.id === 'reviewed' ? 'text-info-500' : 'text-success-500';
    const glyph = renderColoredGlyph(step.id, toneClass);
    if (step.state === 'done') {
        return (
            <span aria-hidden={true} className="inline-flex items-center justify-center opacity-40">
                {glyph}
            </span>
        );
    }
    return glyph;
};

const renderUpcomingGateStamp = (step: AuditTrailStep, isNext: boolean, compactSize: number): ReactNode => {
    const tone = isNext ? (step.id === 'reviewed' ? 'text-info-500' : 'text-success-500') : 'text-gray-500';

    return (
        <span className={`inline-flex items-center justify-center ${tone}`}>
            <DottedOutlineCircleIcon height={compactSize} width={compactSize} />
        </span>
    );
};

const renderStampBody = (step: AuditTrailStep, isNext: boolean, compactSize: number, muted: boolean): ReactNode => {
    if (muted && step.state === 'upcoming') {
        return (
            <span className="inline-flex items-center justify-center text-gray-500">
                <DottedOutlineCircleIcon height={compactSize} width={compactSize} />
            </span>
        );
    }
    const isGateActive = step.id !== 'draft' && (step.state === 'done' || step.state === 'current');
    if (isGateActive) {
        return renderActiveGateStamp(step);
    }
    if (step.state === 'upcoming' && step.id !== 'draft') {
        return renderUpcomingGateStamp(step, isNext, compactSize);
    }
    if (step.state === 'done') {
        return (
            <IconCircleCheck
                aria-hidden={true}
                className="text-gray-500"
                style={{ height: compactSize, width: compactSize }}
            />
        );
    }
    return (
        <span className="inline-flex items-center justify-center text-gray-500">
            <DottedOutlineCircleIcon height={compactSize} width={compactSize} />
        </span>
    );
};

type AuditStampProps = {
    readonly step: AuditTrailStep;
    readonly size?: number;
    readonly isNext?: boolean;
    readonly muted?: boolean;
};

export const AuditStamp: FunctionComponent<AuditStampProps> = ({ isNext = false, muted = false, size = 14, step }) => {
    const compactSize = Math.round(size * 0.82);

    return (
        <span
            aria-hidden={true}
            className="inline-flex shrink-0 items-center justify-center"
            style={{ height: size, width: size }}
        >
            {renderStampBody(step, isNext, compactSize, muted)}
        </span>
    );
};
